import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { resultsAPI, showAPI, scoreAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { Check, X, ArrowsIn, ArrowsOut, CaretDown, CaretUp, ArrowsClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LiveResults = () => {
  const { classId } = useParams();
  const { user } = useAuth();
  const [classInfo, setClassInfo] = useState(null);
  // All classes of the parent show, used to resolve source-class names
  // when rendering "R/O from {Source}" badges on rollover entries.
  const [allShowClasses, setAllShowClasses] = useState([]);
  const [liveData, setLiveData] = useState({ current: null, on_deck: null, in_hole: null });
  const [results, setResults] = useState([]);
  const [divisionStandings, setDivisionStandings] = useState({});
  const [divisionType, setDivisionType] = useState('NONE');
  const [activeTab, setActiveTab] = useState('overall');
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const [payoutInfo, setPayoutInfo] = useState(null);
  const [timeValue, setTimeValue] = useState('');
  const [allEntries, setAllEntries] = useState([]);
  const [entryTimes, setEntryTimes] = useState({});
  const [drawSearch, setDrawSearch] = useState('');
  // Collapse the draw list by default so leaderboard/stream are the focus.
  // On the side-by-side layout (lg+), default to expanded so the producer
  // sees Draw on the left and Standings on the right.
  const [drawCollapsed, setDrawCollapsed] = useState(false);
  const [editingLiveTimeId, setEditingLiveTimeId] = useState(null);
  const timeInputRef = useRef(null);
  // Live-stream display modes: 'sticky' (stays at top while scrolling, capped 50vh)
  // or 'pip' (small floating picture-in-picture card in the corner).
  const [streamMode, setStreamMode] = useState('sticky');
  const [streamHidden, setStreamHidden] = useState(false);

  const isProducer = user && (user.roles?.includes('MASTER') || user.roles?.includes('ORG_ADMIN') || user.roles?.includes('LMASTER') || user.role === 'MASTER');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadData();

    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/ws/live/${classId}`);

    ws.onopen = () => { setWsConnected(true); };
    ws.onmessage = (event) => {
      const message = JSON.parse(event.data);
      if (message.type === 'score_update') loadData(true);
    };
    ws.onerror = () => { setWsConnected(false); };
    ws.onclose = () => { setWsConnected(false); };

    const pollInterval = setInterval(() => { loadData(true); }, 5000);

    return () => { ws.close(); clearInterval(pollInterval); };
  }, [classId]);

  const loadData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const shows = await showAPI.list('PUBLIC');
      let foundShowId = null;
      for (const show of shows.data) {
        const cls = show.classes.find(c => c.id === classId);
        if (cls) {
          setClassInfo({ ...cls, showName: show.name, showLocation: show.location, showId: show.id, video_stream_url: show.video_stream_url || '' });
          setAllShowClasses(show.classes || []);
          foundShowId = show.id;
          break;
        }
      }
      const { data: live } = await resultsAPI.getLive(classId);
      setLiveData(live);
      const { data: resultsData } = await resultsAPI.get(classId);
      setResults(resultsData.results || []);
      setDivisionStandings(resultsData.division_standings || {});
      setDivisionType(resultsData.division_type || 'NONE');
      setPayoutInfo(resultsData.payouts || null);
      // Load all entries with scores for producer scoring
      if (foundShowId) {
        try {
          // v248 — Backend already sorts per class-level draw_reversed.
          const { data: entries } = await import('../utils/api').then(m => m.classEntryAPI.list(foundShowId, classId));
          setAllEntries([...entries].sort((a, b) => (a.draw_number == null) - (b.draw_number == null)));
        } catch { /* ignore */ }
      }
    } catch (error) {
      console.error('Error loading live results:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const divisionNames = useMemo(() => Object.keys(divisionStandings).sort(), [divisionStandings]);
  const hasDivisions = divisionType !== 'NONE' && divisionNames.length > 0;
  const displayResults = useMemo(() => activeTab === 'overall' ? results : (divisionStandings[activeTab] || []), [activeTab, results, divisionStandings]);

  const filteredDrawEntries = useMemo(() => {
    if (!drawSearch.trim()) return allEntries;
    const q = drawSearch.toLowerCase();
    return allEntries.filter(e =>
      e.rider_name.toLowerCase().includes(q) ||
      (e.horse_name && e.horse_name.toLowerCase().includes(q))
    );
  }, [allEntries, drawSearch]);

  const handleSubmitTime = async (entryId, overrideStatus) => {
    if (!entryId) return;
    const val = (entryTimes[entryId] || timeValue).trim();
    const scrMarkers = ['scr', 'scratch'];
    const ntMarkers = ['nt', 'no time', 'dns', 'dnf', 'to'];
    let time = null;
    let scoreStatus = overrideStatus || 'COMPLETED';
    if (scrMarkers.includes(val.toLowerCase()) || overrideStatus === 'SCR') {
      scoreStatus = 'SCR';
    } else if (ntMarkers.includes(val.toLowerCase()) || overrideStatus === 'NT') {
      scoreStatus = 'NT';
    } else if (overrideStatus && overrideStatus !== 'COMPLETED') {
      scoreStatus = overrideStatus;
    } else {
      time = parseFloat(val);
      if (isNaN(time) || time <= 0) return;
    }
    // Work out the next un-timed entry in current draw order BEFORE submitting,
    // so after loadData we can focus that row's input.
    const currentOrder = (filteredDrawEntries.length ? filteredDrawEntries : allEntries) || [];
    const currentIdx = currentOrder.findIndex(e => e.id === entryId);
    const nextEntry = currentOrder.slice(currentIdx + 1).find(e => {
      const s = e.score;
      if (!s) return true;
      return !(s.status && s.status !== 'COMPLETED' ? true : s.final_time != null);
    });
    try {
      await scoreAPI.submit({ entry_id: entryId, class_id: classId, time, knocks: 0, status: scoreStatus });
      toast.success(scoreStatus === 'SCR' ? 'Scratch recorded' : scoreStatus === 'NT' ? 'NT recorded' : `${time?.toFixed(3)} recorded`);
      setTimeValue('');
      setEntryTimes(prev => ({ ...prev, [entryId]: '' }));
      loadData(true);
      // Auto-advance: after loadData renders, focus the next un-timed input.
      // `loadData` is async but doesn't await; a small timeout is enough for
      // the DOM to update. We keep the input selected so the next keystroke
      // replaces whatever stale value is in there.
      if (nextEntry) {
        setTimeout(() => {
          const nextInput = document.querySelector(`[data-testid="score-time-input-${nextEntry.id}"]`);
          if (nextInput) {
            nextInput.focus();
            try { nextInput.select(); } catch { /* no-op on non-text inputs */ }
          }
        }, 250);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit time');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="shimmer h-12 w-12 rounded-full"></div>
      </div>
    );
  }

  const getPayoutForPlace = (place, divName) => {
    if (!payoutInfo) return null;
    if (divName && divName !== 'overall' && payoutInfo.division_payouts?.[divName]) {
      const divPayouts = payoutInfo.division_payouts[divName].payouts;
      if (Array.isArray(divPayouts)) {
        const p = divPayouts.find(p => p.place === place);
        return p ? p.amount : null;
      }
      return divPayouts?.[String(place)] || null;
    }
    // Overall payouts — dict keyed by string place number
    const overall = payoutInfo.overall_payouts || payoutInfo.payouts;
    if (Array.isArray(overall)) {
      const p = overall.find(p => p.place === place);
      return p ? p.amount : null;
    }
    if (overall && typeof overall === 'object') {
      return overall[String(place)] || overall[place] || null;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0096FF] to-[#00D4FF] py-4">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black text-white" style={{fontFamily: 'Outfit'}} data-testid="live-show-name">
              {classInfo?.showName}
            </h1>
            <p className="text-white/80 text-sm">{classInfo?.name} {classInfo?.showLocation && `\u2022 ${classInfo.showLocation}`}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`w-3 h-3 rounded-full ${wsConnected ? 'bg-white animate-pulse' : 'bg-red-400 animate-pulse'}`}></span>
            <span className="text-white font-semibold text-sm">{wsConnected ? 'LIVE' : 'RECONNECTING'}</span>
          </div>
        </div>
      </div>

      {/* Video stream — stays on screen while scrolling.
          Sticky mode: pinned to top (max 50vh tall).
          PiP mode: small floating player in bottom-right corner.
          Hidden: gone until user clicks the "Show stream" chip. */}
      {classInfo?.video_stream_url && !streamHidden && (
        <div
          className={
            streamMode === 'pip'
              ? 'fixed bottom-4 right-4 z-40 w-[280px] sm:w-[340px] shadow-2xl'
              : 'sticky top-0 z-30 max-w-7xl mx-auto px-4 mt-6 backdrop-blur'
          }
          data-testid="live-stream-container"
        >
          <div
            className={`bg-[#0A0A0A] border border-[#262626] rounded-lg overflow-hidden ${streamMode === 'sticky' ? 'mx-auto' : ''}`}
            style={streamMode === 'sticky' ? { maxWidth: 'min(100%, calc(50vh * 16 / 9))' } : {}}
          >
            <div className="flex items-center gap-2 px-3 py-2 bg-[#161616] border-b border-[#262626]">
              <span className="text-white font-bold text-xs" style={{fontFamily: 'Outfit'}}>Live Stream</span>
              <span className="text-[#FF3B30] text-[9px] font-bold px-1.5 py-0.5 bg-[#FF3B30]/10 rounded-full animate-pulse">LIVE</span>
              <div className="ml-auto flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setStreamMode(streamMode === 'pip' ? 'sticky' : 'pip')}
                  className="p-1 text-[#9E9E9E] hover:text-white"
                  title={streamMode === 'pip' ? 'Expand stream' : 'Minimize to corner'}
                  data-testid="stream-toggle-mode"
                >
                  {streamMode === 'pip' ? <ArrowsOut size={12} /> : <ArrowsIn size={12} />}
                </button>
                <button
                  type="button"
                  onClick={() => setStreamHidden(true)}
                  className="p-1 text-[#9E9E9E] hover:text-[#FF3B30]"
                  title="Hide stream"
                  data-testid="stream-hide"
                >
                  <X size={12} />
                </button>
              </div>
            </div>
            <div style={{
              aspectRatio: '16/9',
              maxHeight: streamMode === 'pip' ? 'none' : '50vh',
            }}>
              <iframe
                src={(() => {
                  const url = classInfo.video_stream_url;
                  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/live\/)([a-zA-Z0-9_-]+)/);
                  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1&mute=1`;
                  const fbMatch = url.match(/facebook\.com.*\/videos\//);
                  if (fbMatch) return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&autoplay=true&mute=1`;
                  return url;
                })()}
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Live Stream"
              />
            </div>
          </div>
        </div>
      )}
      {classInfo?.video_stream_url && streamHidden && (
        <button
          type="button"
          onClick={() => { setStreamHidden(false); setStreamMode('sticky'); }}
          className="fixed bottom-4 right-4 z-40 px-3 py-2 bg-[#FF3B30] text-white text-[10px] font-bold rounded-full shadow-xl hover:bg-[#FF3B30]/90 flex items-center gap-1"
          data-testid="stream-show"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Show Stream
        </button>
      )}

      <div className="max-w-7xl xl:max-w-[1400px] mx-auto px-4 py-4 sm:py-6">
        {/* Current Rider Display - compact */}
        <div className="mb-4">
          <div className="bg-gradient-to-r from-[#00D4FF]/20 to-[#0096FF]/10 border-2 border-[#00D4FF] rounded-lg p-4 sm:p-6 text-center">
            <p className="text-[#00D4FF] text-xs sm:text-sm font-semibold mb-1 uppercase tracking-wide">Now Running</p>
            <p className="text-3xl sm:text-4xl font-black text-white mb-1" style={{fontFamily: 'Outfit'}} data-testid="current-rider-display">
              {liveData.current?.rider_name || 'Waiting...'}
            </p>
            {liveData.current?.horse_name && (
              <p className="text-base sm:text-lg text-[#9E9E9E]">on {liveData.current.horse_name}</p>
            )}
            <p className="text-base sm:text-lg text-[#9E9E9E]">
              Draw #{liveData.current?.draw_number || '-'}
            </p>
          </div>

          <div className="grid grid-cols-4 gap-2 mt-3">
            {(liveData.next_up || []).slice(0, 4).map((rider, idx) => (
              <div key={rider.entry_id} className="bg-[#161616] border border-[#262626] rounded-lg p-2.5 text-center" data-testid={`next-up-${idx}`}>
                <p className="text-[#9E9E9E] font-semibold uppercase text-[9px] mb-0.5">#{rider.draw_number}</p>
                <p className="text-sm font-bold text-white truncate" style={{fontFamily: 'Outfit'}}>{rider.rider_name}</p>
                {rider.horse_name && <p className="text-[#525252] text-[10px] truncate">{rider.horse_name}</p>}
              </div>
            ))}
            {(!liveData.next_up || liveData.next_up.length === 0) && (
              <div className="col-span-4 text-center py-2 text-[#525252] text-xs">No more riders</div>
            )}
          </div>
        </div>

        {/* Side-by-side layout — Draw on the left, Leaderboard on the right
            on lg+ screens. On mobile the two sections naturally stack. */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:items-start">

          {/* Draw List with Search — visible to all, scoring for producers only. Collapsible. */}
          <div className="lg:order-1">
          {allEntries.length > 0 ? (
            <div className="bg-[#161616] border border-[#262626] rounded-lg overflow-hidden lg:sticky lg:top-4" data-testid="draw-list-panel">
              <button
                type="button"
                onClick={() => setDrawCollapsed(!drawCollapsed)}
                className="w-full px-4 py-3 bg-[#0096FF]/10 border-b border-[#262626] flex items-center justify-between hover:bg-[#0096FF]/15 transition-colors"
                data-testid="draw-list-toggle"
              >
                <div className="flex items-center gap-2">
                  {drawCollapsed ? <CaretDown size={14} className="text-[#0096FF]" /> : <CaretUp size={14} className="text-[#0096FF]" />}
                  <p className="text-[#0096FF] text-sm font-bold uppercase tracking-wider">Draw ({allEntries.length})</p>
                </div>
                <span className="text-[10px] text-[#555]">{allEntries.filter(e => e.score?.status).length} scored</span>
              </button>
              {!drawCollapsed && (
                <>
                  {/* Search */}
                  <div className="px-3 py-2 border-b border-[#262626]">
                    <input
                      type="text"
                      placeholder="Search rider or horse..."
                      value={drawSearch}
                      onChange={(e) => setDrawSearch(e.target.value)}
                      className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#0096FF] placeholder:text-[#555]"
                      data-testid="draw-search-input"
                    />
                  </div>
                  <div className="divide-y divide-[#262626]/50 max-h-[400px] overflow-y-auto" id="draw-scroll-container">
                {filteredDrawEntries.map((entry) => {
                  const hasScore = entry.score?.status;
                  const isScored = hasScore === 'COMPLETED';
                  const isScratch = hasScore === 'SCRATCH';
                  const isHighlighted = drawSearch && (
                    entry.rider_name.toLowerCase().includes(drawSearch.toLowerCase()) ||
                    (entry.horse_name && entry.horse_name.toLowerCase().includes(drawSearch.toLowerCase()))
                  );
                  return (
                    <div key={entry.id} className={`flex items-center gap-3 px-4 py-2.5 transition-colors ${isHighlighted ? 'bg-[#0096FF]/10' : ''}`} data-testid={`draw-entry-${entry.id}`} id={`draw-entry-${entry.id}`}>
                      <span className="w-7 h-7 flex items-center justify-center bg-[#0096FF]/10 text-[#0096FF] rounded-full text-xs font-black flex-shrink-0">
                        {entry.draw_number || '-'}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-semibold truncate">{entry.rider_name}</p>
                        {(entry.horse_name || (Array.isArray(entry.rolled_to) && entry.rolled_to.length > 0) || entry.is_rollover) && (
                          <div className="flex items-center gap-1 flex-wrap mt-0.5">
                            {entry.horse_name && <p className="text-[#555] text-[10px] truncate min-w-0">{entry.horse_name}</p>}
                            {/* Rollover-source badge (matches the rolled-to
                                style on the same row so the rider+horse
                                name stays readable). */}
                            {entry.is_rollover && (() => {
                              const src = entry.rollover_source_class_id
                                ? (allShowClasses || []).find((c) => c.id === entry.rollover_source_class_id)
                                : null;
                              const label = src ? src.name : 'previous class';
                              return (
                                <span
                                  className="inline-flex items-center px-1 py-0 bg-[#FF9500]/15 text-[#FF9500] rounded text-[9px] font-bold normal-case leading-none flex-shrink-0"
                                  data-testid={`live-rollover-badge-${entry.id}`}
                                  title={`Rolled from: ${label}`}
                                >
                                  R/O from {label}
                                </span>
                              );
                            })()}
                            {Array.isArray(entry.rolled_to) && entry.rolled_to.length > 0 && (() => {
                              const labels = entry.rolled_to.map(r => r.class_name || r.sidepot_name || 'Unknown').join(', ');
                              const isSidepot = entry.rolled_to.some(r => r.sidepot_id);
                              return (
                                <span
                                  className={`inline-flex items-center px-1 py-0 rounded text-[9px] font-bold normal-case leading-none flex-shrink-0 ${isSidepot ? 'bg-[#FACC15]/15 text-[#FACC15]' : 'bg-[#FF9500]/15 text-[#FF9500]'}`}
                                  data-testid={`live-rolled-to-badge-${entry.id}`}
                                  title={`Rolled to: ${labels}`}
                                >
                                  R/O {labels}
                                </span>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {editingLiveTimeId === entry.id ? (
                          /* Editing mode */
                          <>
                            <input
                              type="text"
                              placeholder="Time or NT"
                              value={entryTimes[entry.id] || ''}
                              onChange={(e) => setEntryTimes(prev => ({ ...prev, [entry.id]: e.target.value }))}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); handleSubmitTime(entry.id); setEditingLiveTimeId(null); }
                                if (e.key === 'Escape') { setEditingLiveTimeId(null); setEntryTimes(prev => ({ ...prev, [entry.id]: '' })); }
                              }}
                              className="w-20 px-2 py-1 bg-[#0A0A0A] border border-[#0096FF] rounded text-white text-xs font-mono text-center focus:outline-none"
                              data-testid={`score-time-input-${entry.id}`}
                              autoFocus
                            />
                            <button onClick={() => { handleSubmitTime(entry.id); setEditingLiveTimeId(null); }}
                              className="p-1 bg-[#34C759] hover:bg-[#2DB84D] rounded" data-testid={`score-save-${entry.id}`}>
                              <Check size={12} weight="bold" className="text-white" />
                            </button>
                            <button onClick={() => { setEditingLiveTimeId(null); setEntryTimes(prev => ({ ...prev, [entry.id]: '' })); }}
                              className="p-1 bg-[#262626] hover:bg-[#333] rounded">
                              <X size={12} className="text-[#9E9E9E]" />
                            </button>
                          </>
                        ) : isScored ? (
                          isProducer ? (
                            <button
                              onClick={() => { setEditingLiveTimeId(entry.id); setEntryTimes(prev => ({ ...prev, [entry.id]: entry.score.final_time?.toFixed(3) || '' })); }}
                              className="px-2 py-1 bg-[#34C759]/10 border border-[#34C759]/30 rounded text-[#34C759] text-xs font-mono font-bold hover:bg-[#34C759]/20 transition-colors cursor-pointer"
                              title="Click to edit time"
                              data-testid={`score-time-display-${entry.id}`}
                            >
                              {entry.score.final_time?.toFixed(3)}
                            </button>
                          ) : (
                            <span className="px-2 py-1 bg-[#34C759]/10 border border-[#34C759]/30 rounded text-[#34C759] text-xs font-mono font-bold">
                              {entry.score.final_time?.toFixed(3)}
                            </span>
                          )
                        ) : isScratch ? (
                          isProducer ? (
                            <button
                              onClick={() => { setEditingLiveTimeId(entry.id); setEntryTimes(prev => ({ ...prev, [entry.id]: 'NT' })); }}
                              className="px-2 py-1 bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded text-[#FF3B30] text-xs font-bold hover:bg-[#FF3B30]/20 transition-colors cursor-pointer"
                              title="Click to edit"
                              data-testid={`score-time-display-${entry.id}`}
                            >
                              NT
                            </button>
                          ) : (
                            <span className="px-2 py-1 bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded text-[#FF3B30] text-xs font-bold">NT</span>
                          )
                        ) : isProducer ? (
                          <>
                            <input
                              type="text"
                              placeholder="Time or NT"
                              value={entryTimes[entry.id] || ''}
                              onChange={(e) => setEntryTimes(prev => ({ ...prev, [entry.id]: e.target.value }))}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSubmitTime(entry.id); } }}
                              className="w-20 px-2 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-white text-xs font-mono text-center focus:outline-none focus:border-[#34C759] placeholder:text-[#555]"
                              data-testid={`score-time-input-${entry.id}`}
                            />
                            {entryTimes[entry.id]?.trim() && (
                              <button onClick={() => handleSubmitTime(entry.id)}
                                className="p-1 bg-[#34C759] hover:bg-[#2DB84D] rounded" data-testid={`score-save-${entry.id}`}>
                                <Check size={12} weight="bold" className="text-white" />
                              </button>
                            )}
                            <button onClick={() => handleSubmitTime(entry.id, 'NT')}
                              className="px-1.5 py-1 text-[#FF3B30] text-[10px] font-bold hover:bg-[#FF3B30]/10 rounded" data-testid={`score-nt-${entry.id}`}>
                              NT
                            </button>
                          </>
                        ) : (
                          <span className="text-[#555] text-xs" data-testid={`score-not-run-${entry.id}`}>—</span>
                        )}
                      </div>
                    </div>
                  );
                })}
                {filteredDrawEntries.length === 0 && drawSearch && (
                  <div className="px-4 py-6 text-center text-[#555] text-sm">No results for "{drawSearch}"</div>
                )}
              </div>
                </>
              )}
            </div>
          ) : (
            <div className="bg-[#161616] border border-[#262626] rounded-lg p-8 text-center" data-testid="draw-list-empty">
              <p className="text-[#555] text-sm">Draw not yet available — sign in as a producer to see and score the draw here.</p>
            </div>
          )}
          </div>

        {/* Leaderboard */}
        <div className="lg:order-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-black text-white" style={{fontFamily: 'Outfit'}} data-testid="leaderboard-title">
              Leaderboard
            </h2>
            <div className="text-right">
              <span className="text-[#9E9E9E] text-sm">{results.length} Competitor{results.length !== 1 ? 's' : ''}</span>
              {payoutInfo && payoutInfo.total_pool > 0 && (
                <p className="text-[#34C759] text-sm font-bold" data-testid="pool-amount">
                  Pool: ${payoutInfo.total_pool.toLocaleString()}
                  {activeTab !== 'overall' && payoutInfo.division_payouts?.[activeTab] && (
                    <span className="text-[#9E9E9E] font-normal"> (Div: ${payoutInfo.division_payouts[activeTab].pool.toLocaleString()})</span>
                  )}
                </p>
              )}
            </div>
          </div>

          {/* Division Tabs */}
          {hasDivisions && (
            <div className="flex gap-1 mb-4 overflow-x-auto pb-1" data-testid="division-tabs">
              <button
                onClick={() => setActiveTab('overall')}
                data-testid="tab-overall"
                className={`px-4 py-2 rounded-md text-sm font-semibold whitespace-nowrap transition-colors ${
                  activeTab === 'overall' ? 'bg-[#0096FF] text-white' : 'bg-[#161616] border border-[#262626] text-[#9E9E9E] hover:text-white'
                }`}
              >
                Overall
              </button>
              {divisionNames.sort().map((divName) => (
                <button
                  key={divName}
                  onClick={() => setActiveTab(divName)}
                  data-testid={`tab-${divName.toLowerCase()}`}
                  className={`px-4 py-2 rounded-md text-sm font-semibold whitespace-nowrap transition-colors ${
                    activeTab === divName ? 'bg-[#0096FF] text-white' : 'bg-[#161616] border border-[#262626] text-[#9E9E9E] hover:text-white'
                  }`}
                >
                  {divName}
                </button>
              ))}
            </div>
          )}

          <div className="space-y-2">
            {displayResults.length === 0 ? (
              <div className="bg-[#161616] border border-[#262626] rounded-lg p-12 text-center">
                <p className="text-[#9E9E9E] text-lg">
                  {activeTab === 'overall' ? 'No results yet. Competition in progress...' : `No results in ${activeTab} yet`}
                </p>
              </div>
            ) : (
              displayResults.map((result, index) => {
                const isNT = result.status === 'NT' || result.final_time === null;
                const isSCR = result.status === 'SCR';
                const place = (isNT || isSCR) ? null : (result.place || (index + 1));
                const isTied = result.tied || false;
                const payout = (place && activeTab !== 'overall') ? getPayoutForPlace(place, activeTab) : null;
                return (
                  <div
                    key={index}
                    data-testid={`leaderboard-row-${index}`}
                    className={`bg-[#161616] border-2 rounded-lg p-5 transition-all duration-300 ${
                      isSCR ? 'border-[#FF3B30]/20 opacity-60' :
                      isNT ? 'border-[#262626] opacity-70' :
                      place === 1 ? 'border-[#FFD700] bg-gradient-to-r from-[#FFD700]/10 to-transparent' :
                      place === 2 ? 'border-[#C0C0C0] bg-gradient-to-r from-[#C0C0C0]/10 to-transparent' :
                      place === 3 ? 'border-[#CD7F32] bg-gradient-to-r from-[#CD7F32]/10 to-transparent' :
                      'border-[#262626]'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2 overflow-hidden">
                      <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                        <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center relative flex-shrink-0 ${
                          isSCR ? 'bg-[#FF3B30]/10' :
                          isNT ? 'bg-[#262626]' :
                          place === 1 ? 'bg-[#FFD700]/20' :
                          place === 2 ? 'bg-[#C0C0C0]/20' :
                          place === 3 ? 'bg-[#CD7F32]/20' :
                          'bg-[#0A0A0A]'
                        }`}>
                          <span className={`text-base sm:text-lg font-black ${
                            isSCR ? 'text-[#FF3B30]' :
                            isNT ? 'text-[#9E9E9E]' :
                            place === 1 ? 'text-[#FFD700]' :
                            place === 2 ? 'text-[#C0C0C0]' :
                            place === 3 ? 'text-[#CD7F32]' :
                            'text-[#9E9E9E]'
                          }`} style={{fontFamily: 'Outfit'}}>{isSCR ? 'SCR' : isNT ? 'NT' : place}</span>
                          {isTied && !isNT && !isSCR && (
                            <span className="absolute -top-1.5 -right-1.5 bg-[#FF9500] text-black text-[9px] font-black rounded-full w-4 h-4 flex items-center justify-center" data-testid={`tie-badge-${index}`}>T</span>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm sm:text-base font-bold text-white truncate" style={{fontFamily: 'Outfit'}}>{result.rider_name}</p>
                          <div className="flex items-center gap-2 text-[#9E9E9E] text-[10px] sm:text-xs">
                            {result.horse_name && <span className="italic truncate">{result.horse_name}</span>}
                            {isSCR && <span className="text-[#FF3B30] font-semibold flex-shrink-0">Scratch</span>}
                            {isNT && !isSCR && <span className="text-[#9E9E9E] font-semibold flex-shrink-0">No Time</span>}
                            {!isNT && !isSCR && isTied && <span className="text-[#FF9500] font-semibold flex-shrink-0">TIE</span>}
                            {!isNT && !isSCR && result.knocks > 0 && <span className="text-[#FF3B30] flex-shrink-0">{result.knocks}K</span>}
                            {!isNT && !isSCR && activeTab === 'overall' && result.division && (
                              <span className="px-1.5 py-0.5 bg-[#0096FF]/15 text-[#0096FF] rounded text-[10px] font-semibold flex-shrink-0">{result.division}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-lg sm:text-xl font-black text-white" style={{fontFamily: 'JetBrains Mono'}}>
                          {isSCR ? 'SCR' : isNT ? 'NT' : result.final_time?.toFixed(3)}
                        </p>
                        {payout ? (
                          <p className="text-[#34C759] text-sm font-bold mt-1" data-testid={`payout-${index}`}>
                            ${payout.toLocaleString(undefined, {minimumFractionDigits: 2})}
                            {isTied && <span className="text-[#FF9500] text-xs font-normal ml-1">ea</span>}
                          </p>
                        ) : (
                          <p className="text-[#9E9E9E] text-xs mt-1">seconds</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-[#161616] border-t border-[#262626] mt-12 py-6">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-[#9E9E9E]">Powered by <span className="text-[#0096FF] font-semibold">Show Master</span></p>
        </div>
      </div>
    </div>
  );
};

export default LiveResults;
